import type { ReplitDashboardData } from '@/types/replit-dashboard';

/**
 * Epic to Replit Data Adapter
 * Transforms Epic FHIR data to Replit component format
 */

// Type for FHIR data from Epic API
interface EpicFHIRData {
  sessionId?: string | number;
  demographics?: {
    id?: string;
    name?: string;
    birthDate?: string;
    gender?: string;
    mrn?: string;
  };
  organizationName?: string;
  labs?: Array<{
    id?: string;
    test?: string;
    result?: number | string;
    unit?: string;
    referenceRange?: string;
    interpretation?: string;
    date?: string;
    loincCode?: string | null;
  }>;
  vitals?: Array<{
    date?: string;
    type?: string;
    value?: number;
    unit?: string;
  }>;
  conditions?: Array<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  medications?: Array<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  clinicalNotes?: Array<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  imaging?: Array<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  careTeam?: Array<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  allergies?: Array<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  procedures?: Array<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  encounters?: Array<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  immunizations?: Array<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export function transformEpicToReplit(
  epicData: EpicFHIRData,
  sessionId?: string | number
): ReplitDashboardData {
  // Use sessionId from server (Express session ID) or fall back to 1
  const actualSessionId = epicData.sessionId || sessionId || 1;

  return {
    // Patient demographic data
    patient: {
      id: epicData.demographics?.id || '',
      firstName: epicData.demographics?.name?.split(' ')[0] || 'Patient',
      lastName: epicData.demographics?.name?.split(' ').slice(1).join(' ') || '',
      dateOfBirth: epicData.demographics?.birthDate || '',
      gender: epicData.demographics?.gender || '',
      mrn: epicData.demographics?.mrn || '',
      currentStatus: generateCurrentStatus(epicData),
      admissionDate: extractAdmissionDate(epicData),
      room: extractRoomNumber(epicData),
      attendingPhysician: extractAttendingPhysician(epicData)
    },

    // Session metadata
    session: {
      id: actualSessionId,
      patientId: epicData.demographics?.id || '',
      organizationName: epicData.organizationName || 'Healthcare Organization',
      lastSync: new Date().toISOString()
    },

    // Lab results (from Observation resources) - transformed for LabResultsTable
    labResults: (epicData.labs || []).map((lab) => ({
      id: lab.id || crypto.randomUUID(),
      name: lab.test || 'Unknown Test',
      value: typeof lab.result === 'number' ? lab.result.toString() : (lab.result || 'N/A'),
      unit: lab.unit || '',
      referenceRange: lab.referenceRange || 'N/A',
      status: mapInterpretationToStatus(
        lab.interpretation,
        lab.result,
        lab.unit,
        lab.referenceRange
      ),
      date: lab.date || new Date().toISOString(),
      abnormal: mapInterpretationToStatus(
        lab.interpretation,
        lab.result,
        lab.unit,
        lab.referenceRange
      ) !== 'normal',
      loincCode: lab.loincCode || undefined
    })),

    // Vital signs (from FHIR Observation resources) - transformed to match ReplitVitalSign interface
    vitalSigns: (epicData.vitals || []).map((vital) => ({
      id: crypto.randomUUID(),
      type: vital.type || 'Unknown',
      value: vital.value?.toString() || '',
      unit: vital.unit || '',
      recordedAt: vital.date || new Date().toISOString()
    })),

    // Latest vitals (most recent readings grouped by type)
    latestVitals: extractLatestVitals(epicData.vitals || []),

    // Conditions (diagnoses)
    // Handle both flattened data from fhir-client.ts (strings) and raw FHIR objects
    conditions: (epicData.conditions || []).map((condition) => ({
      id: condition.id || crypto.randomUUID(),
      name: typeof condition.code === 'string'
        ? condition.code
        : (condition.code?.text || condition.code?.coding?.[0]?.display || 'Unknown Condition'),
      clinicalStatus: typeof condition.clinicalStatus === 'string'
        ? condition.clinicalStatus
        : (condition.clinicalStatus?.coding?.[0]?.code || 'unknown'),
      verificationStatus: typeof condition.verificationStatus === 'string'
        ? condition.verificationStatus
        : (condition.verificationStatus?.coding?.[0]?.code || 'unknown'),
      onsetDate: condition.onsetDate || condition.onsetDateTime || condition.recordedDate || '',
      severity: typeof condition.severity === 'string'
        ? condition.severity
        : (condition.severity?.coding?.[0]?.display || 'Unknown')
    })),

    // Medications
    medications: (epicData.medications || []).map((med) => ({
      id: med.id || crypto.randomUUID(),
      // FHIR client already transforms the data, so use the 'medication' field directly
      name: med.medication ||
            med.medicationCodeableConcept?.text ||
            med.medicationCodeableConcept?.coding?.[0]?.display ||
            'Unknown Medication',
      dosage: med.dosageInstructions ||
              med.dosageInstruction?.[0]?.text ||
              '',
      status: med.status || 'unknown',
      prescribedDate: med.authoredOn || ''
    })),

    // Documents (from clinicalNotes - FHIR DocumentReference resources)
    // Map to format expected by ClinicalNotes component
    documents: (epicData.clinicalNotes || []).map((doc) => ({
      id: doc.id || crypto.randomUUID(),
      authorName: doc.author || 'Unknown Provider',
      authorTitle: '', // FHIR doesn't always provide this
      noteType: doc.type || 'Clinical Note',
      content: typeof doc.content === 'string' ? doc.content : (doc.description || 'Document content not available'),
      createdAt: doc.date || new Date().toISOString(),
      // Preserve additional fields for potential future use
      category: doc.category,
      status: doc.status,
      contentType: doc.contentType,
      isBinary: doc.isBinary
    })),

    // Imaging studies (from DiagnosticReport and ImagingStudy resources)
    // Pass through all fields from FHIR client since ImagingStudies component expects them
    imagingStudies: (epicData.imaging || []).map((study) => ({
      id: study.id || crypto.randomUUID(),
      type: study.type || study.code?.text || 'Imaging Study',
      category: study.category || 'Imaging',
      date: study.date || study.started || '',
      status: study.status || 'final',
      description: study.description || '',
      performer: study.performer || 'Unknown',
      results: study.results || null,
      attachments: study.attachments || null,
      hasAttachments: study.hasAttachments || false,
      source: study.source || 'ImagingStudy',
      // ImagingStudy-specific fields (will be undefined for DiagnosticReports)
      modality: study.modality?.code || '',
      numberOfSeries: study.numberOfSeries || 0,
      numberOfInstances: study.numberOfInstances || 0
    })),

    // Care team
    careTeam: (epicData.careTeam || []).map((team) => ({
      id: team.id || crypto.randomUUID(),
      name: team.name || 'Care Team',
      status: team.status || 'active',
      category: team.category || 'General Care',
      members: (team.members || []).map((member: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
        name: member.name || 'Unknown Member',
        roles: member.roles || ['Unknown Role'],
        startDate: member.startDate || null,
        endDate: member.endDate || null
      })),
      period: {
        start: team.period?.start || null,
        end: team.period?.end || null
      },
      managingOrganization: team.managingOrganization || []
    })),

    // Allergies (from AllergyIntolerance resources)
    allergies: (epicData.allergies || []).map((allergy) => ({
      id: allergy.id || crypto.randomUUID(),
      substance: allergy.code?.text ||
                 allergy.code?.coding?.[0]?.display ||
                 'Unknown Allergen',
      criticality: allergy.criticality || 'unknown',
      clinicalStatus: allergy.clinicalStatus?.coding?.[0]?.code || 'active',
      verificationStatus: allergy.verificationStatus?.coding?.[0]?.code || 'unknown',
      type: allergy.type || 'allergy',
      category: allergy.category?.[0] || 'unknown',
      recordedDate: allergy.recordedDate || '',
      reactions: (allergy.reaction || []).map((reaction: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
        manifestation: reaction.manifestation?.[0]?.text ||
                       reaction.manifestation?.[0]?.coding?.[0]?.display ||
                       'Unknown Reaction',
        severity: reaction.severity || 'unknown',
        onset: reaction.onset || null
      }))
    })),

    // Encounters (already flattened by the FHIR client) — passed through so the
    // server sees the current admission when this object is posted as chat context
    encounters: epicData.encounters || [],

    // Procedures (from Procedure resources)
    procedures: (epicData.procedures || []).map((procedure) => ({
      id: procedure.id || crypto.randomUUID(),
      procedure: procedure.code?.text ||
                 procedure.code?.coding?.[0]?.display ||
                 'Unknown Procedure',
      status: procedure.status || 'unknown',
      performedDate: procedure.performedDateTime ||
                     procedure.performedPeriod?.start ||
                     '',
      performer: procedure.performer?.[0]?.actor?.display ||
                 procedure.performer?.[0]?.actor?.reference ||
                 'Unknown Performer',
      category: procedure.category?.coding?.[0]?.display ||
                procedure.category?.text ||
                'Procedure',
      reasonCode: procedure.reasonCode?.[0]?.text ||
                  procedure.reasonCode?.[0]?.coding?.[0]?.display ||
                  null,
      bodySite: procedure.bodySite?.[0]?.text ||
                procedure.bodySite?.[0]?.coding?.[0]?.display ||
                null
    }))
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate lab status by comparing result value against reference range
 * @param result - The lab result value (number or string)
 * @param referenceRange - The reference range string (e.g., "0.2-1.2 mg/dL", "> 10", "< 5")
 * @returns Status string: 'high', 'low', or 'normal', or null if cannot be determined
 */
function calculateStatusFromRange(
  result: number | string | undefined,
  referenceRange: string | undefined
): string | null {
  // Cannot calculate if either value is missing
  if (!result || !referenceRange || referenceRange === 'N/A') {
    return null;
  }

  // Convert result to number if it's a string
  const numericResult = typeof result === 'number' ? result : parseFloat(String(result));

  // Skip if result is not numeric (e.g., "Positive", "Detected", "Negative")
  if (isNaN(numericResult)) {
    return null;
  }

  // Parse reference range - handle various formats:
  // "0.2-1.2 mg/dL" → low=0.2, high=1.2
  // "> 10" → low=10, high=null
  // "< 5" → low=null, high=5
  // "10-20" → low=10, high=20

  const rangeStr = referenceRange.trim();

  // Try to match "X-Y" pattern (range)
  const rangeMatch = rangeStr.match(/^(\d+\.?\d*)\s*-\s*(\d+\.?\d*)/);
  if (rangeMatch) {
    const low = parseFloat(rangeMatch[1]);
    const high = parseFloat(rangeMatch[2]);

    if (!isNaN(low) && !isNaN(high)) {
      if (numericResult < low) return 'low';
      if (numericResult > high) return 'high';
      return 'normal';
    }
  }

  // Try to match "> X" pattern (greater than threshold)
  const gtMatch = rangeStr.match(/^>\s*=?\s*(\d+\.?\d*)/);
  if (gtMatch) {
    const threshold = parseFloat(gtMatch[1]);
    if (!isNaN(threshold)) {
      return numericResult >= threshold ? 'normal' : 'low';
    }
  }

  // Try to match "< X" pattern (less than threshold)
  const ltMatch = rangeStr.match(/^<\s*=?\s*(\d+\.?\d*)/);
  if (ltMatch) {
    const threshold = parseFloat(ltMatch[1]);
    if (!isNaN(threshold)) {
      return numericResult <= threshold ? 'normal' : 'high';
    }
  }

  // Could not parse reference range
  return null;
}

/**
 * Maps FHIR Observation interpretation to UI status values
 * Falls back to calculating status from reference range if interpretation is missing
 * @param interpretation - The interpretation string from FHIR (e.g., "High", "Low", "Normal", "Positive")
 * @param result - The lab result value (for fallback calculation)
 * @param unit - The unit of measurement (unused currently, for future use)
 * @param referenceRange - The reference range (for fallback calculation)
 * @returns Status string for UI: 'normal', 'low', 'high', 'critical'
 */
function mapInterpretationToStatus(
  interpretation: string | undefined,
  result?: number | string,
  _unit?: string,
  referenceRange?: string
): string {
  // First, try to use FHIR interpretation if available
  if (interpretation) {
    const normalized = interpretation.toLowerCase().trim();

    // Map FHIR interpretation codes/text to UI status
    // See: http://hl7.org/fhir/valueset-observation-interpretation.html

    // Critical values
    if (normalized.includes('critical') || normalized === 'hh' || normalized === 'll') {
      return 'critical';
    }

    // High values (H = High, HH = Critical High, A = Abnormal if context suggests high)
    if (normalized.includes('high') || normalized === 'h' || normalized.startsWith('h ')) {
      return 'high';
    }

    // Low values (L = Low, LL = Critical Low)
    if (normalized.includes('low') || normalized === 'l' || normalized.startsWith('l ')) {
      return 'low';
    }

    // Abnormal (need to determine if high or low - default to high for safety)
    if (normalized === 'a' || normalized === 'abnormal') {
      return 'high';  // Conservative default
    }

    // Normal values (N = Normal, NEG = Negative)
    if (normalized === 'n' || normalized === 'normal' ||
        normalized === 'neg' || normalized === 'negative') {
      return 'normal';
    }

    // Positive results (for tests like PCR, cultures) - treat as abnormal/detected
    if (normalized === 'pos' || normalized === 'positive' || normalized === 'detected') {
      return 'high';  // Use 'high' to indicate abnormal/positive result
    }

    // If interpretation exists but is unknown, still fall through to range-based calculation
  }

  // Fallback: Calculate status from reference range if available
  const calculatedStatus = calculateStatusFromRange(result, referenceRange);
  if (calculatedStatus) {
    return calculatedStatus;
  }

  // Default to normal if we can't determine status from either interpretation or range
  return 'normal';
}

function generateCurrentStatus(epicData: EpicFHIRData): string {
  // Generate a comprehensive human-readable current status from available data
  const statusParts: string[] = [];

  // 1. Check active conditions
  const activeConditions = (epicData.conditions || []).filter(
    (c) => c.clinicalStatus?.coding?.[0]?.code === 'active'
  );

  // 2. Get recent encounter information
  const currentEncounter = epicData.encounters?.[0]; // Most recent encounter
  const encounterStatus = currentEncounter?.status;
  const encounterReason = currentEncounter?.reasonForVisit;

  // 3. Get active medications count
  const activeMeds = (epicData.medications || []).filter(
    (m) => m.status === 'active'
  );

  // 4. Check recent vitals for abnormal values
  const recentVitals = epicData.vitals || [];
  const hasRecentVitals = recentVitals.length > 0;

  // Build status narrative
  if (encounterStatus === 'in-progress' || encounterStatus === 'arrived') {
    statusParts.push('Currently hospitalized');

    if (encounterReason && encounterReason !== 'Not Specified') {
      statusParts.push(`for ${encounterReason.toLowerCase()}`);
    }
  } else if (activeConditions.length > 0) {
    const primaryCondition = activeConditions[0];
    const conditionName = primaryCondition.code?.text ||
                          primaryCondition.code?.coding?.[0]?.display ||
                          'medical condition';
    statusParts.push(`Under treatment for ${conditionName.toLowerCase()}`);
  } else {
    statusParts.push('Stable condition');
  }

  // Add medication information
  if (activeMeds.length > 0) {
    const medCount = activeMeds.length;
    statusParts.push(`receiving ${medCount} active medication${medCount > 1 ? 's' : ''}`);
  }

  // Add condition count if multiple
  if (activeConditions.length > 1) {
    statusParts.push(`with ${activeConditions.length} active conditions being monitored`);
  }

  // Add vitals monitoring note if available
  if (hasRecentVitals) {
    statusParts.push('vitals being monitored regularly');
  }

  // Construct final status string
  let status = statusParts[0];
  if (statusParts.length > 1) {
    status = statusParts[0] + ', ' + statusParts.slice(1).join(', ') + '.';
  } else {
    status = status + '.';
  }

  return status;
}

function extractAdmissionDate(epicData: EpicFHIRData): string | undefined {
  // Extract from Encounter resource if available
  const encounter = epicData.encounters?.[0];
  return encounter?.period?.start;
}

function extractRoomNumber(epicData: EpicFHIRData): string | undefined {
  // Extract from Encounter location if available
  const encounter = epicData.encounters?.[0];
  return encounter?.location?.[0]?.location?.display;
}

function extractAttendingPhysician(epicData: EpicFHIRData): string | undefined {
  // Extract from Encounter participant if available
  const encounter = epicData.encounters?.[0];
  const participant = encounter?.participant?.find(
    (p: any) => p.type?.[0]?.coding?.[0]?.code === 'ATND' // eslint-disable-line @typescript-eslint/no-explicit-any
  );
  return participant?.individual?.display;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractLatestVitals(vitals: any[]): any | null {
  // Extract most recent readings grouped by vital type
  if (!vitals || vitals.length === 0) return null;

  // Group vitals by type and get latest for each
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const vitalsByType: Record<string, any> = {};

  vitals.forEach((vital) => {
    const type = vital.type?.toLowerCase();
    if (!type) return;

    const vitalDate = new Date(vital.recordedAt || vital.date || 0);
    const existing = vitalsByType[type];
    const existingDate = existing ? new Date(existing.recordedAt || existing.date || 0) : null;

    if (!existing || (existingDate && vitalDate > existingDate)) {
      vitalsByType[type] = vital;
    }
  });

  // Map to expected format
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = {
    recordedAt: vitals[0]?.recordedAt || vitals[0]?.date || new Date().toISOString()
  };

  if (vitalsByType['temperature']) {
    result.temperature = { value: vitalsByType['temperature'].value, unit: vitalsByType['temperature'].unit };
  }
  if (vitalsByType['heart rate']) {
    result.heartRate = { value: vitalsByType['heart rate'].value, unit: vitalsByType['heart rate'].unit };
  }
  if (vitalsByType['respiratory rate']) {
    result.respiratoryRate = { value: vitalsByType['respiratory rate'].value, unit: vitalsByType['respiratory rate'].unit };
  }
  if (vitalsByType['blood pressure']) {
    result.bloodPressure = { value: vitalsByType['blood pressure'].value, unit: vitalsByType['blood pressure'].unit };
  }
  if (vitalsByType['oxygen saturation'] || vitalsByType['spo2']) {
    const spo2 = vitalsByType['oxygen saturation'] || vitalsByType['spo2'];
    result.oxygenSaturation = { value: spo2.value, unit: spo2.unit };
  }

  return result;
}

